import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Link2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { Creator, HomeContent, ytId } from "@/types";
import { OSChrome } from "@/components/os/OSChrome";

export default function CreatorPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [creator, setCreator] = useState<Creator | null>(null);
  const [content, setContent] = useState<HomeContent | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setCreator(null);
    setNotFound(false);
    api.get(`/creators/${slug}`).then((r) => setCreator(r.data)).catch(() => setNotFound(true));
    api.get("/content/home").then((r) => setContent(r.data)).catch(() => {});
  }, [slug]);

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("LINK COPIED — SHARE THE SHOWREEL");
  };

  if (notFound) {
    return (
      <OSChrome id="creator-404" title="error.exe" tint="butter" maxW="max-w-lg">
        <div className="p-8 text-center">
          <p className="font-pixel text-lg leading-relaxed">404: FILE NOT FOUND</p>
          <p className="mt-4 text-sm text-ink/70">This showreel got moved to the trash bin.</p>
          <button data-testid="notfound-home" onClick={() => nav("/portfolio")} className="btn-chunky mt-6 bg-mint px-5 py-2.5 text-xs">
            BACK TO PORTFOLIO
          </button>
        </div>
      </OSChrome>
    );
  }

  return (
    <OSChrome id="creator" title={`${slug}.exe — showreel`} tint={(creator?.tint as string) || "white"}>
      <div className="p-5 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button data-testid="creator-back" onClick={() => nav("/portfolio")} className="btn-chunky bg-white px-3 py-2 text-[11px]">
            <ArrowLeft size={13} /> ../portfolio
          </button>
          <button data-testid="creator-share" onClick={copyLink} className="btn-chunky bg-butter px-3 py-2 text-[11px]">
            <Link2 size={13} /> COPY LINK
          </button>
        </div>

        {!creator ? (
          <p className="mt-8 font-spacemono text-xs text-ink/60">Loading showreel…</p>
        ) : (
          <>
            <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 data-testid="creator-name" className="font-pixel text-xl leading-relaxed sm:text-3xl">
                  {creator.name.toUpperCase()}
                </h2>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="border-2 border-ink bg-white px-2 py-0.5 font-spacemono text-[10px] font-bold">{creator.niche}</span>
                  <span className="border-2 border-ink bg-butter px-2 py-0.5 font-spacemono text-[10px] font-bold">{creator.metric}</span>
                  {content?.status_published && (
                    <span data-testid="status-tag" className="border-2 border-ink bg-lnpink px-2 py-0.5 font-spacemono text-[10px] font-bold">
                      STATUS: {content.status_text}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-7 flex flex-col gap-6">
              {(creator.videos || []).map((v, i) => (
                <motion.div
                  key={v.id}
                  data-testid={`video-${v.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="border-[3px] border-ink bg-white shadow-hard"
                >
                  <div className="flex items-center justify-between border-b-2 border-ink bg-lnpink px-2.5 py-1.5 font-spacemono text-[10px] font-bold uppercase tracking-wider">
                    <span className="truncate">{v.title}.mov</span>
                    <span aria-hidden>▶ 00:00 / HD</span>
                  </div>
                  <div className="bg-ink p-1.5 sm:p-2">
                    <div className="aspect-video w-full">
                      <iframe
                        title={v.title}
                        src={`https://www.youtube-nocookie.com/embed/${ytId(v.youtube_url)}`}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <p className="text-sm font-semibold">{v.title}</p>
                    <span className="font-spacemono text-[10px] font-bold text-ink/50">EDITED BY LNM</span>
                  </div>
                </motion.div>
              ))}
              {(creator.videos || []).length === 0 && (
                <p className="font-spacemono text-xs text-ink/60">No tapes on the shelf yet — check back soon.</p>
              )}
            </div>

            <div className="mt-9 flex justify-center">
              <button data-testid="creator-cta" onClick={() => nav("/inquire")} className="btn-chunky bg-lnpink px-6 py-3 text-xs sm:text-sm">
                WANT EDITS LIKE THIS? <ArrowRight size={15} />
              </button>
            </div>
          </>
        )}
      </div>
    </OSChrome>
  );
}
